import { type Bone, type Skeleton, Vector3 } from 'three'

/**
 * Foot-floor alignment helpers.
 *
 * `lift_pose_to_floor` — single-frame correction that finds the lowest
 * foot/toe-named bone in world-space, and if it's below `floor_y`, raises
 * the skeleton's root bone(s) by the deficit so feet stop intersecting the
 * floor. Cheap, runs once when invoked from a button.
 *
 * NOT included in v1: per-frame IK foot-lock during animation playback. That
 * needs a proper plant-detection + IK solver + root-motion blending pipeline,
 * which is a separate body of work. The "lift to floor" entry point is the
 * common case (model authored with feet below ground) and a useful stepping
 * stone toward the full feature.
 */

export interface LiftResult {
  applied: boolean
  delta_y: number
  lowest_foot_bone: string | null
}

const FOOT_NAME_PATTERNS: RegExp[] = [
  /foot/i, /toe/i, /ankle/i, /heel/i
]

export class FootFloorAlign {
  static lift_pose_to_floor (skeleton: Skeleton, floor_y: number = 0): LiftResult {
    const feet = collect_foot_bones(skeleton)
    if (feet.length === 0) {
      return { applied: false, delta_y: 0, lowest_foot_bone: null }
    }
    skeleton.bones[0]?.updateMatrixWorld(true)
    const world = new Vector3()
    let lowest_y = Infinity
    let lowest_name: string | null = null
    for (const bone of feet) {
      bone.getWorldPosition(world)
      if (world.y < lowest_y) {
        lowest_y = world.y
        lowest_name = bone.name
      }
    }
    const delta = floor_y - lowest_y
    if (delta <= 0) {
      // Already at or above floor; nothing to do.
      return { applied: false, delta_y: 0, lowest_foot_bone: lowest_name }
    }
    // Lift any root bones (those whose parent isn't a Bone). Most rigs have a
    // single root (pelvis/hips); a few have separate root + pelvis. Lifting
    // every root keeps both behaviours working.
    const roots = skeleton.bones.filter(b => b.parent === null || (b.parent as { isBone?: boolean }).isBone !== true)
    for (const root of roots) {
      root.position.y += delta
      root.updateMatrixWorld(true)
    }
    return { applied: true, delta_y: delta, lowest_foot_bone: lowest_name }
  }
}

function collect_foot_bones (skeleton: Skeleton): Bone[] {
  return skeleton.bones.filter((b) => FOOT_NAME_PATTERNS.some(p => p.test(b.name)))
}
